import { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";

import { columnIsSortedByNumbers, sortListBy } from "utils/common";

import ListPagination from "components/ListPagination";
import ScrollableTable from "components/ScrollableTable";
import { useFilters } from "components/useFilters";
import { ORDER_BY_COLUMN_FILTER_KEY } from "components/useFilters/constants";

import {
  getFilteredColumns,
  getHeaders,
} from "entities/Deal/components/ListTable/utils";
import { IPipelineColumns } from "entities/Deal/sdk";

export interface IListTable {
  rows: { [key: string]: string | null }[];
  columns: Array<IPipelineColumns>;
  showLoading: boolean;
}

const LIMIT = 50;

const ListTable = ({ rows, columns, showLoading }: IListTable) => {
  const [page, setPage] = useState<number>(1);
  const { filters, deleteFilter, updateFilter } = useFilters();
  const selectedSortOption = useMemo(
    () => _.get(filters, [ORDER_BY_COLUMN_FILTER_KEY]),
    [filters]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    },
    [setPage]
  );

  const handleUpdateSortOption = useCallback(
    ({ key: option }: { key: string | undefined }) => {
      if (_.isUndefined(option)) {
        deleteFilter({ key: ORDER_BY_COLUMN_FILTER_KEY });
      } else {
        updateFilter({ key: ORDER_BY_COLUMN_FILTER_KEY, value: option });
      }
    },
    [deleteFilter, updateFilter]
  );

  const isSortedByNumbers = useMemo(
    () =>
      columnIsSortedByNumbers({
        selectedSortOption,
        data: rows,
      }),
    [rows, selectedSortOption]
  );

  const sortedDeals = useMemo(
    () => sortListBy(rows, selectedSortOption, isSortedByNumbers),
    [rows, selectedSortOption, isSortedByNumbers]
  );

  const headers = useMemo(
    () => getHeaders(getFilteredColumns({ columns })),
    [columns]
  );

  const pagesCount = useMemo(
    () => Math.ceil(sortedDeals.length / LIMIT),
    [sortedDeals]
  );

  const limitedDeals = useMemo(
    () => sortedDeals.slice((page - 1) * LIMIT, page * LIMIT),
    [sortedDeals, page]
  );

  useEffect(() => {
    // Reset the pagination each time the filters change
    if (!_.isEmpty(filters)) {
      setPage(1);
    }
  }, [filters]);

  return (
    <>
      <ScrollableTable
        rows={limitedDeals}
        headers={headers}
        sortBy={selectedSortOption}
        setSortBy={handleUpdateSortOption}
        showLoading={showLoading}
        tableDataTestid="deals-table-list"
        dynamicHyphen={true}
        rowDataTestid="deal-data-row"
        rowCellDataTestid="deal-data-cell"
        sx={{
          maxHeight: "calc(100vh - 305px)",
        }}
      />

      <ListPagination
        currentPage={page}
        setPage={handlePageChange}
        pagesCount={pagesCount}
      />
    </>
  );
};

export default ListTable;
